<template>
<div class="animated fadeIn device-detail">
  <div class="row page-header">
    <div class="col-xl-8 col-12">
      <div class="row">
        <div class="col-sm-7 col-12">
          <h2 class="page-title float-left">Flume <small>Sensor Data</small></h2>
        </div>

        <div class="col-sm-5 col-12">
          <json-excel
            type="csv"
            :data="exportData"
            :escapeCsv="false"
            class="float-right d-none d-md-block"
          ><b-button size="md" variant="success">Export</b-button>
          </json-excel>
          <b-button
            v-if="manualReadingsEnabled"
            size="md"
            variant="outline-primary"
            class="float-right mr-2 d-none d-md-inline-block"
            :disabled="localConfig.selectedDevices.length !== 1"
            :title="localConfig.selectedDevices.length === 1 ? 'Add a manual reading for the selected device' : 'Select exactly one device'"
            @click="openManualReadingModal"
          >
            <i class="fa fa-pencil-alt"></i> Add Reading
          </b-button>
          <div class="refresh-select float-md-right float-xs-left mr-2">
            <span class="switch-name">Auto Refresh?</span>
            <label class="switch switch-sm switch-text switch-info float-right mb-0 mr-1">
              <input class="switch-input" type="checkbox" v-model="localConfig.refreshEnabled">
              <span data-on="On" data-off="Off" class="switch-label"></span>
              <span class="switch-handle"></span>
            </label>
          </div>
          <div class="threshold-select float-md-right float-xs-left mr-2">
            <span class="switch-name">Show Thresholds?</span>
            <label class="switch switch-sm switch-text switch-info float-right mb-0 mr-1">
              <input class="switch-input" type="checkbox" v-model="localConfig.thresholdsEnabled">
              <span data-on="On" data-off="Off" class="switch-label"></span>
              <span class="switch-handle"></span>
            </label>
          </div>
        </div>
      </div><!-- /row -->

      <div class="row">
        <div class="col-lg-12">
          <multiselect
            id="device-select"
            v-model="localConfig.selectedDevices"
            :options="formattedDevices"
            :multiple="true"
            track-by="serial"
            label="name"
            placeholder="Select Devices"
          ></multiselect>
          <a v-if="localConfig.selectedDevices.length" href="#" class="small text-muted" @click.prevent="localConfig.selectedDevices = []">Deselect all</a>
        </div>
      </div>

      <div class="row">

        <div class="col-md-6 col-12">
          <div class="date-select mt-3">
            <date-picker
              v-model="dateRange"
              range
              confirm
              type="datetime"
              format="YYYY-MM-DD hh:mm"
              lang="en"
              width="320"
              input-class="form-control"
              range-separator="-"
              :not-after="maxDate"
              :shortcuts="dateShortcuts"
              :time-picker-options="{ start: '00:00', step: '00:30', end: '23:30' }"
            ></date-picker>
          </div>
        </div>
        <div class="col-md-5 col-10">
          <div class="agg-select">
            <b-form-select v-model="localConfig.aggMethod" class="mb-3 mt-3 float-xs-left float-md-right" size="md" style="max-width: 20rem;">
              <option :value="null">Select aggregation options</option>
              <option :value="{period: null, fn: null}">Raw Data (no aggregation)</option>
              <optgroup label="Average">
                <option :value="{period: '15m', fn: 'mean'}">15 Minute Avg</option>
                <option :value="{period: '30m', fn: 'mean'}">30 Minute Avg</option>
                <option :value="{period: '1h', fn: 'mean'}">Hourly Avg</option>
                <option :value="{period: '1d', fn: 'mean'}">Daily Avg</option>
              </optgroup>
              <optgroup label="Max">
                <option :value="{period: '15m', fn: 'max'}">15 Minute Max</option>
                <option :value="{period: '30m', fn: 'max'}">30 Minute Max</option>
                <option :value="{period: '1h', fn: 'max'}">Hourly Max</option>
                <option :value="{period: '1d', fn: 'max'}">Daily Max</option>
              </optgroup>
              <optgroup label="Min">
                <option :value="{period: '15m', fn: 'min'}">15 Minute Min</option>
                <option :value="{period: '30m', fn: 'min'}">30 Minute Min</option>
                <option :value="{period: '1h', fn: 'min'}">Hourly Min</option>
                <option :value="{period: '1d', fn: 'min'}">Daily Min</option>
              </optgroup>
            </b-form-select>
          </div>
        </div>
        <div class="col-md-1 col-2">
          <b-button class="option-submit mt-3 float-right" variant="success" @click="$apollo.queries.metrics.refetch()">Go</b-button>
        </div>

      </div><!-- /row -->

    </div><!-- /col-xl-8 -->

    <div class="col-xl-4 mb-2">
      <GoogleMap
        mapStyle="width:100%;height:140px;"
        :center="primaryDeviceLocation"
        :markers="deviceLocations"
      />
    </div>

  </div>

  <div class="row">
    <div class="col-xl-8 col-md-12">
      <b-card>
        <div class="chart sensor-chart-tall">
          <LineChart
            ref="metricChart"
            v-if="!loading"
            :title="'Cubic Feet/Sec'"
            :scale="'cf/s'"
            :series="getSeries('flow_cfs')"
            :labels="labels"
            :height="400"
          ></LineChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Flow</div>
              <strong>{{ getAverage('flow_cfs') }} cf/s</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="2" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta('flow_cfs') }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="warning" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

    <div class="col-xl-4 col-md-12">
      <b-card class="pt-4">
        <div class="chart gauge-chart-tall">
          <RadialChart
            ref="cfsGaugeChart"
            v-if="!loading"
            :title="'Cubic Feet/sec'"
            :scale="'cf/s'"
            :config="getRadialConfig('flow_cfs')"
          ></RadialChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Latest</div>
              <strong>{{ getValue('flow_cfs') }} cf/s</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="2" variant="success" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

  </div>
  <div class="row">
    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <AreaChart
            ref="depthChart"
            v-if="!loading"
            :title="'Depth'"
            :scale="'feet'"
            :series="getSeries('depth_f')"
            :labels="labels"
          ></AreaChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Depth</div>
              <strong>{{ getAverage('depth_f') }} ft</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="2" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta('depth_f') }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="warning" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <AreaChart
            ref="gpmChart"
            v-if="!loading"
            :title="'Gallons Per Minute'"
            :scale="'gpm'"
            :series="getSeries('flow_gpm')"
            :labels="labels"
          ></AreaChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. GPM</div>
              <strong>{{ getAverage('flow_gpm') }} F</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="2" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta('flow_gpm') }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>
  </div>

  <div class="row">
    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <LineChart
            ref="tdsChart"
            v-if="!loading"
            :title="'Battery %'"
            :scale="'pct'"
            :series="getSeries('bat_chg')"
            :labels="labels"
          ></LineChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Charge</div>
              <strong>{{ getAverage('bat_chg') }} %</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta('bat_chg') }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="warning" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <LineChart
            ref="tempChart"
            v-if="!loading"
            :title="'Case Temperature'"
            :scale="'Fahrenheit'"
            :series="getSeries('tmp_f')"
            :labels="labels"
          ></LineChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Temperature</div>
              <strong>{{ getAverage('tmp_f') }} F</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta('tmp_f') }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>
  </div>

  <div v-if="localConfig.selectedDevices.length === 1" class="row mt-3">
    <div class="col-12">
      <WeatherWidget :lat="primaryDeviceLocation.lat" :lng="primaryDeviceLocation.lng" :date-range="dateRange" />
    </div>
  </div>

  <ManualReadingModal
    :device="manualReadingDevice"
    :visible="showManualReadingModal"
    @close="showManualReadingModal = false"
    @created="onManualReadingChanged"
  />

</div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import LineChart from '@/components/charts/LineChart.vue'
import AreaChart from '@/components/charts/AreaChart.vue'
import RadialChart from '@/components/charts/RadialChart.vue'
import GoogleMap from '@/components/GoogleMap.vue'
import WeatherWidget from '@/components/WeatherWidget.vue'
import ManualReadingModal from '@/components/ManualReadingModal.vue'
import DatePicker from 'vue2-datepicker'
import moment from 'moment-timezone'
import JsonExcel from 'vue-json-excel'
import gql from 'graphql-tag'

import { METRICS_QUERY, MANUAL_READINGS_QUERY } from '@/constants/graphql'
import { DATE_SHORTCUTS, FLUME_THRESHOLDS } from '@/constants/data'
import { MANUAL_READINGS_ENABLED } from '@/constants/features'

const GET_STATE = gql`
  query {
    config @client {
      flume {
        thresholdsEnabled
        refreshEnabled
        aggMethod {
          period
          fn
        }
        selectedDevices {
          name
          deviceName
          serial
        }
      }
    }
  }
`

const MIN_THRESHOLD_LABEL = 'Min. Threshold'
const ALERT_THRESHOLD_LABEL = 'Alert Threshold'

export default {
  name: 'flume',
  props: ['devices', 'defaultDevices'],
  data () {
    return {
      loading: 0,
      error: null,
      maxDate: new Date(Date.now() + 60000 * 30), // show up to 30 minutes in the future
      localConfig: {
        thresholdsEnabled: true,
        refreshEnabled: false,
        aggMethod: {
          period: '1h',
          fn: 'mean'
        },
        selectedDevices: this.defaultDevices
      },
      refreshInterval: 60000,
      refreshTimer: null,
      dateRange: [new Date(Date.now() - 3600 * 1000 * 24 * 7), new Date()], // 1 week back
      dateShortcuts: DATE_SHORTCUTS,
      showManualReadingModal: false,
      manualReadingDevice: null,
      manualReadings: []
    }
  },
  watch: {
    localConfig: {
      handler: function(data) {

        const config = {
          flume: {
            ...data,
            __typename: 'Flume',
            aggMethod: {
              ...data.aggMethod,
              __typename: 'FlumeAggregation'
            },
            selectedDevices: data.selectedDevices.map((d) => {
              return Object.assign({ __typename: 'Device' }, d)
            })
          }
        }

        this.$apollo
          .mutate({
            mutation: gql`
              mutation($config: Config) {
                updateConfig(config: $config) @client
              }
            `,
            variables: {
              config
            }
          })

        this.updateRefresh()
      },
      deep: true
    }
  },
  computed: {
    manualReadingsEnabled () { return MANUAL_READINGS_ENABLED },
    primaryDeviceLocation () {
      return this.deviceLocations[0].position
    },
    deviceLocations () {
      if (this.localConfig.selectedDevices.length) {
        const searchSerials = this.localConfig.selectedDevices.map(x => x.serial)
        const mapDevices = this.devices
          .filter(d => searchSerials.includes(d.serial))
          .map(d => ({ position: { lat: d.latitude, lng: d.longitude } }))

        if (mapDevices.length) {
          return mapDevices
        }
      }
      // [{position: primaryDeviceLocation}]
      return [{ position: { lat: 44.030829, lng: -70.3535825 } }] // bottling plant
    },
    formattedDevices () {
      let deviceList = []
      this.devices.forEach((device) => {
        if(device.type == 'flume') {
          deviceList.push({
            name: device.site.name + ' (Device: ' + device.name + ')',
            deviceName: device.name,
            serial: device.serial
          })
        }
      })
      return deviceList
    },
    selectedSerials () {
      return this.localConfig.selectedDevices.map(x => x.serial)
    },
    labels () {
      let data = (this.metrics && this.metrics.data) ? this.metrics.data : null
      if (data) {
        let device = Object.keys(data)[0]
        if (data && device) return data[device].values.map(x => x[0])
      }
      return []
    },
    exportData () {
      let raw = (this.metrics && this.metrics.data.length) ? this.metrics.data : []
      let processed = []

      let aggPeriod = this.localConfig.aggMethod.period;
      let remainderMinutes = 0

      if (aggPeriod !== null) {
        switch(aggPeriod) {
          case '15m':
            remainderMinutes = 15
            break
          case '30m':
            remainderMinutes = 30
            break
          case '1h':
            remainderMinutes = 60
            break
          case '1d':
            remainderMinutes = 1440
            break
        }
      }

      raw.forEach(d => {
        // set headers if not already set
        let deviceObj = this.getDeviceBySerial(d.tags.device_id)
        let timeIndex = this.getMetricIndex(d, 'time')

        if (!processed.length) {
          processed.push(['device_name', 'site', 'type', 'local_timestamp', ...d.columns])
        }
        d.values.forEach(values => {
          let time = moment(values[timeIndex] * 1000).second(0) // .format('YYYY-MM-DD HH:mm:ss')
          const remainder = remainderMinutes - (time.minute() % remainderMinutes);
          let timeStr

          if (aggPeriod !== null) {
            timeStr = moment(time).add(remainder, "minutes").format("YYYY-MM-DD HH:mm:ss")
          } else {
            timeStr = moment(time).format("YYYY-MM-DD HH:mm:ss")
          }
          // console.log(timeStr)
          // console.log(d.name, time)
          processed.push([deviceObj.name, deviceObj.site.name, d.name, timeStr, ...values])
        })
      })
      return processed
    }
  },
  apollo: {
    config:  {
      query: GET_STATE
    },
    metrics: {
      query: METRICS_QUERY,
      variables () {
        return {
          where: {
            device: this.selectedSerials.join('|'),
            type: 'flume',
            metric: 'depth_f|flow_cfs|flow_gpm|tmp_f|bat_chg',
            start: this.dateRange[0],
            end: this.dateRange[1],
            agg: this.localConfig.aggMethod.fn,
            period: this.localConfig.aggMethod.period,
            sort: 'asc',
            limit: 15000
          }
        }
      },
      skip () {
        return (!this.selectedSerials.length)
      }
    },
    manualReadings: {
      query: MANUAL_READINGS_QUERY,
      variables () {
        return {
          where: {
            deviceSerial_in: this.selectedSerials,
            timestamp_gte: this.dateRange[0],
            timestamp_lte: this.dateRange[1]
          },
          orderBy: { timestamp: 'asc' },
          limit: 5000
        }
      },
      skip () {
        return (!MANUAL_READINGS_ENABLED || !this.selectedSerials.length)
      },
      error () { /* silently ignore */ }
    }
  },
  methods: {
    openManualReadingModal () {
      if (this.localConfig.selectedDevices.length !== 1) return
      const selected = this.localConfig.selectedDevices[0]
      const fullDevice = this.devices.find(d => d.serial === selected.serial)
      this.manualReadingDevice = fullDevice || { serial: selected.serial, name: selected.deviceName, type: 'flume' }
      this.showManualReadingModal = true
    },
    onManualReadingChanged () {
      this.showManualReadingModal = false
      if (this.$apollo.queries.manualReadings) {
        this.$apollo.queries.manualReadings.refetch()
      }
    },
    manualSeriesForMetric (metric) {
      var self = this
      if (!self.manualReadings || !self.manualReadings.length) return []
      var bySerial = {}
      self.manualReadings.forEach(function (r) {
        if (r.metricName !== metric) return
        var serial = (r.device && r.device.serial) || null
        if (!serial) return
        if (!bySerial[serial]) bySerial[serial] = { verified: [], unverified: [] }
        var bucket = r.isFieldVerified ? 'verified' : 'unverified'
        bySerial[serial][bucket].push([new Date(r.timestamp).getTime(), r.value])
      })
      var series = []
      Object.keys(bySerial).forEach(function (serial) {
        var deviceObj = self.getDeviceBySerial(serial)
        var label = (deviceObj && deviceObj.name) || serial
        if (bySerial[serial].verified.length) {
          series.push({
            name: label + ' (field verified)',
            type: 'scatter',
            marker: { symbol: 'diamond', radius: 6, fillColor: '#28a745', lineColor: '#1d6e34', lineWidth: 1 },
            lineWidth: 0,
            data: bySerial[serial].verified
          })
        }
        if (bySerial[serial].unverified.length) {
          series.push({
            name: label + ' (manual)',
            type: 'scatter',
            marker: { symbol: 'diamond', radius: 6, fillColor: '#ffc107', lineColor: '#a8800a', lineWidth: 1 },
            lineWidth: 0,
            data: bySerial[serial].unverified
          })
        }
      })
      return series
    },
    thresholdValue (deviceName, epoch, metric='flow_gpm') {
      let thresholds = FLUME_THRESHOLDS

      if(thresholds.hasOwnProperty(deviceName)) {
        let thresholdValues = thresholds[deviceName]
        let dateObject = new Date(epoch*1000)

        if (dateObject) {
          let value = thresholdValues[dateObject.getMonth()]
          if (metric == 'flow_gpm') return value;
          if (metric == 'flow_cfs') return value / 448.8309375;
        }
      }

      return 0
    },
    updateRefresh () {
      if (this.localConfig.refreshEnabled && !this.refreshTimer) {
        this.refreshTimer = setInterval(() => {
          // push out date for date picker
          this.maxDate = new Date(Date.now() + 60000 * 30)
          this.dateRange = [
            moment(this.dateRange[0]).add(this.refreshInterval, 'ms').toDate(),
            moment(this.dateRange[1]).add(this.refreshInterval, 'ms').toDate()
          ]
          this.$apollo.queries.metrics.refetch()
        }, this.refreshInterval)
      } else {
        clearInterval(this.refreshTimer)
      }
    },
    getSeries (metric) {
      // console.time('getSeries')
      metric = metric || 'flow_cfs'
      const timeIndex = 2

      let series = []
      let metricName = (this.localConfig.aggMethod && this.localConfig.aggMethod.fn)
        ? `${this.localConfig.aggMethod.fn}_${metric}`
        : metric

      if (!this.metrics || !this.metrics.data) {
        return series
      }

      Object.keys(this.metrics.data).forEach((device) => {
        let deviceData = this.metrics.data[device]
        let deviceObj = this.getDeviceBySerial(deviceData.tags.device_id)
        let metricIndex = this.getMetricIndex(deviceData, metricName)

        series.push({
          name: (deviceObj) ? deviceObj.name : deviceData.tags.device_id,
          data: deviceData.values.map(x => [x[timeIndex] * 1000, x[metricIndex]])
        })

        if (this.localConfig.thresholdsEnabled && (metric == 'flow_gpm' || metric == 'flow_cfs')) {
          series.push({
            name: ((deviceObj) ? deviceObj.name : deviceData.tags.device_id) + ' - ' + MIN_THRESHOLD_LABEL,
            data: deviceData.values.map(x => [x[timeIndex] * 1000, this.thresholdValue(deviceObj.name, x[timeIndex], metric) * 1.2]),
            lineWidth: 1,
            dashStyle: "Dash",
            lineColor: 'black',
            marker: {
              enabled: false
            }
          })

          series.push({
            name: ((deviceObj) ? deviceObj.name : deviceData.tags.device_id) + ' - ' + ALERT_THRESHOLD_LABEL,
            data: deviceData.values.map(x => [x[timeIndex] * 1000, this.thresholdValue(deviceObj.name, x[timeIndex], metric)]),
            lineWidth: 1,
            // // dashStyle: "Dash",
            lineColor: 'red',
            marker: {
              enabled: false
            }
          })
        }
      })

      // Overlay manual readings as additional scatter series
      var manualSeries = this.manualSeriesForMetric(metric)
      if (manualSeries.length) {
        manualSeries.forEach(function (s) { series.push(s) })
      }

      // console.timeEnd('getSeries')
      return series
    },
    getRadialConfig (metric) {
      let categories = []
      Object.keys(this.metrics.data).forEach((device) => {
        let deviceData = this.metrics.data[device]
        let deviceObj = this.getDeviceBySerial(deviceData.tags.device_id)
        categories.push( (deviceObj) ? deviceObj.name : deviceData.tags.device_id )
      })

      let config = {
        min: 0,
        max: 7,
        categories,
        series: this.getRadialSeries(categories, metric)
      }
      return config
    },
    getRadialSeries (deviceNames, metric) {
      let series = this.getSeries(metric)

      if (series.length === 1) {
        // only one device loaded, no thresholds present
        return [{
          name: 'cf/s',
          data: [this.getValue(metric, true)]
        }]
      } else {
        let thresholdConfig = {}
        series.map((el) => {
          if (el.name.includes(ALERT_THRESHOLD_LABEL)) {
            let name = el.name.split(' ')[0]
            thresholdConfig[name] = {
              'min': el.data[el.data.length - 1][1],
              'max': el.data[el.data.length - 1][1] * 4
            }
          }
        })

        let validSeries = series.filter((el) => !el.name.toLowerCase().includes('threshold'))
        return [{
          name: 'cf/s',
          data: deviceNames.map((name) => {
            let series = validSeries.filter(el => el.name == name)
            if (!series || series.length == 0) return null

            let max = (thresholdConfig.hasOwnProperty(name)) ? thresholdConfig[name].max : 7
            let y = parseInt(series[0].data[series[0].data.length - 1][1] * 100) / 100
            let color = (max === 7 || max === 0) ? '#7cb5ec' : this.percentToColor((y / max) * 100)
            return {
              y,
              color
            }
          })
        }]
      }
    },
    percentToColor (percent) {
      let r, g, b = 0;
      if (percent > 100) percent = 100;
      if (percent < 25) return '#ee3333';
      if(percent < 50) {
        r = 255;
        g = Math.round(5.1 * percent);
      }
      else {
        g = 255;
        r = Math.round(510 - 5.10 * percent);
      }
      let h = r * 0x10000 + g * 0x100 + b * 0x1;
      return '#' + ('000000' + h.toString(16)).slice(-6);
    },
    getValue (metric, latest) {
      metric = metric || 'ec'
      latest = (latest !== undefined) ? latest : true

      let data = this.getSeries(metric)
      if (!data || data.length === 0) {
        return null
      }

      let validSeries = data.filter((el) => !el.name.toLowerCase().includes('threshold'))
      let values = validSeries.map(d => {
        let point = (latest) ? d.data[d.data.length - 1][1] : d.data[0][1]
        return parseInt(point * 100) / 100
      })

      return (values.length) ? parseInt((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100 : 0
    },
    getAverage (metric) {
      metric = metric || 'ec'
      let data = this.getSeries(metric)

      if (!data || !data[0]) {
        return null
      }

      let validSeries = data.filter((el) => !el.name.toLowerCase().includes('threshold'))
      let values = validSeries.map(d => {
        return d.data.reduce((a, b) => a + b[1], 0) / d.data.length
      })

      return (values.length) ? parseInt((values.reduce((a, b) => a + b, 0) / values.length) * 100 ) / 100 : 0
    },
    getDelta (metric) {
      metric = metric || 'ec'
      let start = this.getValue(metric, false)
      let end = this.getValue(metric, true)

      if (start === 0) {
        return '0%'
      }

      let delta = 100 * Math.abs( ( end - start ) / ( (end + start) / 2 ) )

      if (delta === 0) {
        return '0%'
      } else {
        return (delta > 0) ? `+${delta.toFixed(1)}%` : `${delta.toFixed(2)}%`
      }
    },
    getDeviceBySerial (serial) {
      return this.devices.filter((device) => {
        return device.serial === serial
      })[0]
    },
    getMetricIndex (deviceData, metricName) {
      for (let i in deviceData.columns) {
        if (deviceData.columns[i] === metricName) {
          return i
        }
      }
      return -1
    }
  },
  beforeMount () {
    if (this.config && this.config.flume) {
      this.localConfig = JSON.parse(JSON.stringify(this.config.flume))
    }
    if (!this.localConfig.selectedDevices || !this.localConfig.selectedDevices.length) {
      this.localConfig.selectedDevices = this.defaultDevices
    }
  },
  beforeDestroy () {
    clearInterval(this.refreshTimer)
  },
  components: {
    Multiselect,
    LineChart,
    AreaChart,
    RadialChart,
    DatePicker,
    JsonExcel,
    GoogleMap,
    WeatherWidget,
    ManualReadingModal
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
</style>
